import React, {useState, useEffect} from "react";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Close";
import Button from "@material-ui/core/Button";

import TestValueEditor from "./value_editor";
import TestResults from "../test_results";

import {Test, TestResult} from "common/flowmaster/types/test";
import {FormControl} from "@material-ui/core";
import {LlmRunId} from "common/flowmaster/types/llm_run";
import {OrganisationId} from "common/types/organisation";

interface Props {
  llmRunId: LlmRunId;
  organisationId: OrganisationId;
  test: Test;
  results: TestResult[];
  onUpdate: (type: string, value: Record<string, unknown>) => void;
  onDelete: () => void;
}

export default function TestEditor({
  test,
  results,
  onUpdate,
  onDelete,
  llmRunId,
  organisationId,
}: Props) {
  // State to store original and current test states
  const [originalTest, setOriginalTest] = useState<Test>(test);
  const [currentTest, setCurrentTest] = useState<Test>(test);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setOriginalTest(test);
    setCurrentTest(test);
  }, [test]);

  const handleTestTypeChange = (type: string) => {
    setCurrentTest(prevTest => ({...prevTest, test_type: type}));
    setHasChanges(true);
  };

  const handleTestValueChange = (value: Record<string, unknown>) => {
    setCurrentTest(prevTest => ({...prevTest, value}));
    setHasChanges(true);
  };

  const handleSave = () => {
    if (hasChanges) {
      onUpdate(currentTest.test_type, currentTest.value);
      setOriginalTest(currentTest);
      setHasChanges(false);
    }
  };

  useEffect(() => {
    const isChanged =
      JSON.stringify(originalTest) !== JSON.stringify(currentTest);
    setHasChanges(isChanged);
  }, [currentTest, originalTest]);

  const thisRunResult = results.find(result => result.llm_run_id === llmRunId);

  return (
    <div
      style={{
        padding: "0.5em",
        border: "1px solid #999",
        borderRadius: "1em",
        display: "flex",
        flexDirection: "column",
        gap: "0.5em",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: 10,
          margin: "auto",
        }}
      >
        <TestType
          type={currentTest.test_type}
          onChange={handleTestTypeChange}
        />
        <TestValueEditor test={currentTest} onChange={handleTestValueChange} />
        <DeleteButton onClick={onDelete} />

        {hasChanges && (
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        )}
      </div>
      <div>
        <TestResults
          llmRunId={llmRunId}
          results={results}
          organisationId={organisationId}
        />
      </div>
      {thisRunResult?.state === "fail" && (
        <div style={{display: "flex", gap: "0.5em"}}>
          <span style={{fontWeight: "bold", marginRight: "0.5em"}}>Error:</span>
          <span>{thisRunResult.detail.message}</span>
        </div>
      )}
    </div>
  );
}

function TestType({type, onChange}) {
  return (
    <FormControl variant="outlined">
      <Select value={type} onChange={e => onChange(e.target.value)}>
        <MenuItem value="exact_match">Exact Match</MenuItem>
        <MenuItem value="regex">Regex</MenuItem>
        <MenuItem value="applicable_clauses">Applicable clauses</MenuItem>
        <MenuItem value="json">JSON</MenuItem>
        <MenuItem value="find_item">Find Item</MenuItem>
      </Select>
    </FormControl>
  );
}

function DeleteButton({onClick}) {
  return (
    <IconButton onClick={onClick}>
      <DeleteIcon />
    </IconButton>
  );
}
